import React, { ChangeEvent } from 'react';
import { Caption } from '../Caption/Caption';
import { Icon } from '../../icons';
import './Toggle.css';

export interface ToggleProps {
  label?: string;
  caption?: React.ReactNode;
  successMessage?: string;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  leadingLabel?: string;
  trailingLabel?: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  ariaLabel?: string;
}

export const Toggle = React.memo(function Toggle({
  label,
  caption,
  successMessage,
  errorMessage,
  disabled = false,
  required = false,
  leadingLabel,
  trailingLabel,
  checked,
  onChange,
  id = `toggle-${Math.random().toString(36).substring(2, 9)}`,
  ariaLabel,
}: ToggleProps) {
  const className = `toggle ${errorMessage ? 'toggle--error' : ''} ${checked ? 'toggle--checked' : ''} ${disabled ? 'toggle--disabled' : ''}`;

  return (
    <div className={className} aria-label={ariaLabel}>
      {label && (
        <label htmlFor={id} className="toggle__label">
          {label}
        </label>
      )}
      <div className="toggle__switch">
        {leadingLabel && (
          <div className="toggle__status toggle__status--leading">{leadingLabel}</div>
        )}
        <label className="toggle__control" htmlFor={id} aria-label={ariaLabel || 'Toggle switch'}>
          <input
            id={id}
            type="checkbox"
            disabled={disabled}
            required={required}
            checked={checked}
            onChange={onChange}
          />
          <div className="toggle__icons">
            <div className="toggle__line-icon">
              <Icon name="Minus" size={10} />
            </div>
            <div className="toggle__circle-icon">
              <Icon name="Circle" size={10} />
            </div>
          </div>
          <div className="toggle__knob" />
        </label>
        {trailingLabel && (
          <div className="toggle__status toggle__status--trailing">{trailingLabel}</div>
        )}
      </div>
      {caption && <Caption>{caption}</Caption>}
      {errorMessage && (
        <span className="toggle__message toggle__message--error">{errorMessage}</span>
      )}
      {successMessage && (
        <span className="toggle__message toggle__message--success">{successMessage}</span>
      )}
    </div>
  );
});
